import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, cx } from '@/components/primitives';
import { SIGN_IN_DESTINATIONS } from './SignInMenu';
import { useDismissableMenu } from './useDismissableMenu';

export interface NavLinkItem {
  to: string;
  label: string;
  /** One line under the label, in a desktop menu only. */
  hint?: string;
}

export interface NavSection {
  label: string;
  /** A section with no children is a plain link. */
  to?: string;
  items?: NavLinkItem[];
}

/**
 * The public navigation, as a structure rather than a row of links.
 *
 * The header carried five flat links while the site had eighteen public pages,
 * so services, verification, fraud reporting, contact and the FAQ were
 * reachable only from the footer. Grouping is what lets those surface without
 * the header becoming a wall.
 *
 * The job entries deliberately point at filtered searches. The list accepts
 * fifteen filters and nothing pointed a candidate at any of them, so "urgent
 * hiring" and "freshers welcome" do double duty: they are useful destinations,
 * and they show that the filters exist.
 */
export function publicNavSections(t: (key: string, fallback: string) => string): NavSection[] {
  return [
    {
      label: t('nav.findJobs', 'Find jobs'),
      items: [
        { to: '/jobs', label: t('nav.allJobs', 'All vacancies'), hint: 'Everything live right now' },
        {
          to: '/jobs?isUrgent=true',
          label: t('nav.urgentJobs', 'Urgent hiring'),
          hint: 'Employers who need people this week',
        },
        {
          to: '/jobs?isBulkHiring=true',
          label: t('nav.bulkJobs', 'Bulk hiring drives'),
          hint: 'Walk-in drives and large intakes',
        },
        {
          to: '/jobs?maxExperienceMonths=0',
          label: t('nav.fresherJobs', 'Freshers welcome'),
          hint: 'No experience needed',
        },
        {
          to: '/jobs?postedWithinDays=7',
          label: t('nav.recentJobs', 'Posted this week'),
          hint: 'The newest vacancies first',
        },
      ],
    },
    {
      label: t('nav.companies', 'Companies'),
      items: [
        { to: '/companies', label: t('nav.allCompanies', 'All employers') },
        {
          to: '/companies?hiringNow=true',
          label: t('nav.hiringNow', 'Hiring now'),
          hint: 'Employers with live vacancies',
        },
        {
          to: '/employer-verification',
          label: t('nav.howWeVerify', 'How we verify employers'),
          hint: 'What our verified badge means',
        },
      ],
    },
    {
      label: t('nav.forSeekers', 'Job seekers'),
      items: [
        { to: '/job-seekers', label: t('nav.howItWorks', 'How it works') },
        {
          to: '/safety',
          label: t('nav.safety', 'Your safety'),
          hint: 'We never charge a fee, at any stage',
        },
        { to: '/report-fraud', label: t('nav.reportFraud', 'Report fraud') },
        { to: '/faq', label: t('nav.faq', 'Questions') },
      ],
    },
    {
      label: t('nav.forEmployers', 'For employers'),
      items: [
        { to: '/employers', label: t('nav.whyUs', 'Why work with us') },
        { to: '/services', label: t('nav.services', 'Our services') },
        { to: '/employer-verification', label: t('nav.getVerified', 'Getting verified') },
        { to: '/contact', label: t('nav.contact', 'Talk to us') },
      ],
    },
    { label: t('nav.about', 'About'), to: '/about' },
  ];
}

/**
 * One header menu.
 *
 * Opens on click rather than hover: hover menus are unreachable by keyboard
 * without extra handling, and on the touch devices most of this audience uses
 * the first tap becomes a hover that swallows the second.
 */
function NavMenu({ section }: { section: NavSection }) {
  const { open, setOpen, wrapper, id } = useDismissableMenu();
  const location = useLocation();

  const active = section.items?.some((item) => location.pathname === item.to.split('?')[0]);

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className={cx(
          'flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          active || open ? 'text-brand-700' : 'text-ink-600 hover:text-ink-900',
        )}
      >
        {section.label}
        <Icon
          name="chevronDown"
          size="xs"
          className={cx('transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          id={id}
          className="absolute left-0 top-full z-30 mt-1 w-72 rounded-lg bg-white p-1.5 shadow-lg ring-1 ring-ink-200 motion-safe:animate-toast-in"
        >
          {section.items?.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="block rounded-md px-3 py-2 hover:bg-brand-50 focus-visible:outline focus-visible:outline-2"
            >
              <span className="block text-sm font-medium text-ink-900">{item.label}</span>
              {item.hint && <span className="mt-0.5 block text-xs text-ink-500">{item.hint}</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/** The header row, from the medium breakpoint up. */
export function PublicNavBar() {
  const { t } = useTranslation();

  return (
    <nav aria-label="Main" className="hidden items-center gap-0.5 md:flex">
      {publicNavSections(t).map((section) =>
        section.to ? (
          <NavLink
            key={section.label}
            to={section.to}
            className={({ isActive }) =>
              cx(
                'rounded-md px-3 py-1.5 text-sm font-medium',
                isActive ? 'text-brand-700' : 'text-ink-600 hover:text-ink-900',
              )
            }
          >
            {section.label}
          </NavLink>
        ) : (
          <NavMenu key={section.label} section={section} />
        ),
      )}
    </nav>
  );
}

/**
 * The phone navigation.
 *
 * There was none. The header's only nav was `hidden md:flex`, with no drawer
 * behind it — so on a phone, which is how most of this audience arrives, the
 * header offered the logo, the language switcher and sign-in, and every other
 * page on the site was unreachable from it.
 */
export function PublicNavDrawer() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKeyDown);
    // The page behind must not scroll under the drawer.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [open]);

  return (
    <div className="md:hidden">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('nav.openMenu', 'Open menu')}
        aria-expanded={open}
        className="grid h-11 w-11 place-items-center rounded-md text-ink-700 hover:bg-ink-100 focus-visible:outline focus-visible:outline-2"
      >
        <Icon name="menu" size="lg" />
      </button>

      {open && (
        <div className="fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-ink-900/40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('nav.menu', 'Menu')}
            className="absolute inset-y-0 right-0 flex w-[min(20rem,85vw)] flex-col bg-white shadow-xl"
          >
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-ink-200 px-4">
              <span className="text-sm font-semibold text-ink-900">{t('nav.menu', 'Menu')}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('nav.closeMenu', 'Close menu')}
                className="grid h-11 w-11 place-items-center rounded-md text-ink-600 hover:bg-ink-100 focus-visible:outline focus-visible:outline-2"
              >
                <Icon name="close" size="lg" />
              </button>
            </div>

            {/* Every section expanded rather than a stack of accordions: on a
                phone an extra tap per section is worse than a scroll. */}
            <nav aria-label="Main" className="flex-1 overflow-y-auto px-2 py-3">
              {publicNavSections(t).map((section) => (
                <div key={section.label} className="mb-3">
                  {section.to ? (
                    <Link
                      to={section.to}
                      className="block rounded-md px-3 py-2.5 text-sm font-semibold text-ink-900 hover:bg-ink-50"
                    >
                      {section.label}
                    </Link>
                  ) : (
                    <>
                      <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-ink-500">
                        {section.label}
                      </p>
                      {section.items?.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className="block rounded-md px-3 py-2.5 text-sm text-ink-700 hover:bg-ink-50"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </>
                  )}
                </div>
              ))}
            </nav>

            {/* The drawer listed eighteen public pages and offered no way into
                an account. On a phone — how most of this audience arrives —
                signing in meant rotating the handset to reach the header.

                Pinned below the scroll rather than inside it, because a way in
                should not be something you have to scroll to find. */}
            <div className="shrink-0 border-t border-ink-200 bg-ink-50 p-3">
              <div className="flex flex-col gap-2">
                {SIGN_IN_DESTINATIONS.map((destination) => (
                  <Link
                    key={destination.audience}
                    to={destination.to}
                    className={cx(
                      'flex min-h-11 items-center rounded-md border-l-[3px] bg-white px-3 py-2.5',
                      'text-sm font-medium text-ink-900 ring-1 ring-inset ring-ink-500',
                      'focus-visible:outline focus-visible:outline-2',
                      destination.edge,
                    )}
                  >
                    {destination.label}
                  </Link>
                ))}
                {/* Hidden in the header below the `sm` breakpoint, so without
                    this a phone cannot reach the employer flow at all. */}
                <Link
                  to="/employer/signup"
                  className="flex min-h-11 items-center justify-center rounded-md bg-action-600 px-3 py-2.5 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2"
                >
                  {t('nav.hireWorkers', 'Hire workers')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
